/**
 * The open numeric answer, as a keypad rather than a text field (change 6).
 *
 * WHY A KEYPAD AND NOT A WHEEL, A SCRUBBER OR AN INPUT.
 *
 *   - A text field is out by the brief, and rightly: a software keyboard on a
 *     phone eats half the viewport, arrives late, and puts a "done" key
 *     somewhere different on every OS. Under an eight-second clock that is not
 *     an input, it is a penalty.
 *   - A scrubber or slider cannot express an EXACT value, and `un-year` and
 *     `land-borders` are graded EXACT-only. An input that physically cannot hit
 *     the answer is not an input.
 *   - A digit WHEEL can hit an exact value, but spinning four wheels under a
 *     clock is slow, and momentum scrolling makes the last digit a fight. It
 *     also fails one-handed: the wheels sit in a column and the top one is out
 *     of thumb reach on a 6" phone.
 *   - A keypad is 12 fixed targets that never move, every one reachable, every
 *     one a place the thumb already knows from the phone dialler. It is the
 *     fastest thing on touch that can still be exact.
 *
 * WHAT THE PUBLIC BOUNDS BUY. Every numeric quantity ships `min` and `max` in
 * `NumericAnswerSpec`, and those are constants of the QUANTITY, identical for
 * every country (see the invariant-5 note on that type). So the pad can grey
 * out any digit that cannot begin a legal answer. None of that derives from the
 * answer: it is a pure function of `min` and `max`, which the client was
 * already shown, so it narrows nothing and leaks nothing.
 *
 * WHAT THE PAD USED TO DO AND NO LONGER DOES: type for you. When exactly one
 * digit could legally follow, an earlier version appended it on your behalf —
 * tap 1 on `un-year` and the readout jumped straight to "19", because nothing
 * but 9 can follow a leading 1 inside [1945, 2011]. It saved a tap, and it was
 * wrong for a reason no amount of correctness in the bounds can fix: a hand
 * reaching for a four-digit year has already committed to four taps. The 9 is
 * in flight before the readout has changed. It lands on a run that is already
 * "19", making "199", and a player who meant 1945 is now typing 1994 with three
 * seconds left. The saving was one tap; the cost was a whole golden rung, paid
 * by the player who typed the year the ordinary way.
 *
 * So the rule is now flat: ONE TAP, ONE DIGIT. Nothing reaches the readout that
 * a thumb did not put there, and typing a number left to right always produces
 * that number. The forced digit is still known — `forcedDigit` names it, and
 * the pad lights that key — but lighting a key is an invitation the hand can
 * take at its own speed, where typing the digit is a decision taken out of the
 * hand's control. Four taps for a year, and every one of them predicted.
 */

import type { NumericAnswerSpec } from '@geogame/shared';

/** The ten keys. Strings, because the value under construction is a digit run. */
export const DIGIT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const;
export type DigitKey = (typeof DIGIT_KEYS)[number];

/** Longest run of digits the control will accept for a quantity. */
export function maxDigits(spec: NumericAnswerSpec): number {
  return Math.max(1, Math.min(9, Math.trunc(spec.digits)));
}

function pow10(n: number): number {
  return 10 ** n;
}

/**
 * Could this run of digits still become a legal answer?
 *
 * A run `p` of length k can be completed to any length L in [k, maxDigits],
 * which spans the closed interval [p·10^(L−k), p·10^(L−k) + 10^(L−k) − 1]. The
 * run is viable when ANY of those intervals meets [min, max].
 */
export function isViablePrefix(spec: NumericAnswerSpec, digits: string): boolean {
  if (digits === '') return true;
  if (!/^[0-9]+$/.test(digits)) return false;
  const limit = maxDigits(spec);
  if (digits.length > limit) return false;
  // A leading zero is only ever the WHOLE answer. "07" is not how anyone types
  // seven, and allowing it doubles the keystrokes that reach a value. Because
  // nothing may follow a zero, a run starting with one is viable only when zero
  // itself is in range — otherwise the pad would offer a key leading nowhere.
  if (digits.startsWith('0')) return digits === '0' && spec.min <= 0 && spec.max >= 0;
  const p = Number(digits);
  for (let len = digits.length; len <= limit; len += 1) {
    const scale = pow10(len - digits.length);
    const lo = p * scale;
    const hi = lo + scale - 1;
    if (hi >= spec.min && lo <= spec.max) return true;
  }
  return false;
}

/** Digits that may be appended to `digits` right now. */
export function legalNextDigits(spec: NumericAnswerSpec, digits: string): readonly DigitKey[] {
  return DIGIT_KEYS.filter((d) => isViablePrefix(spec, digits + d));
}

/** True when the run, exactly as typed, is a submittable value. */
export function isSubmittable(spec: NumericAnswerSpec, digits: string): boolean {
  if (digits === '' || !/^[0-9]+$/.test(digits)) return false;
  if (digits.length > 1 && digits.startsWith('0')) return false;
  const v = Number(digits);
  return Number.isFinite(v) && v >= spec.min && v <= spec.max;
}

/** The value under construction, or null when there is nothing legal yet. */
export function valueOf(spec: NumericAnswerSpec, digits: string): number | null {
  return isSubmittable(spec, digits) ? Number(digits) : null;
}

/**
 * The one digit that must come next, when the bounds leave no choice at all.
 *
 * This is the old auto-advance rule, demoted from an action to a HINT. The pad
 * lights this key instead of pressing it, which keeps the shortcut in front of
 * the eye without taking the tap out of the hand.
 *
 * Two conditions, both load bearing. There must be exactly one legal next
 * digit, and the run so far must not already BE a legal answer — the moment
 * stopping is an option the player is choosing, and a lit key would be the pad
 * leaning on that choice. For `un-year`, "1" is not a year in [1945, 2011], so
 * 9 is forced and lights. For `land-borders`, "1" IS a legal answer, so nothing
 * lights and the player picks between 1, 10, 11, 12, 13 and 14.
 */
export function forcedDigit(spec: NumericAnswerSpec, digits: string): DigitKey | null {
  if (!isViablePrefix(spec, digits)) return null;
  if (isSubmittable(spec, digits)) return null;
  const only = legalNextDigits(spec, digits);
  return only.length === 1 ? (only[0] as DigitKey) : null;
}

/**
 * Append a digit — one tap, one digit, and nothing else.
 *
 * A tap that cannot begin a legal answer is dropped rather than applied. Its
 * key is greyed out anyway, so this is the belt to that pair of braces: a
 * hardware keyboard, a synthetic event, a stale render.
 */
export function pushDigit(spec: NumericAnswerSpec, digits: string, key: DigitKey): string {
  const next = digits + key;
  return isViablePrefix(spec, next) ? next : digits;
}

/** Drop the last digit. */
export function backspace(digits: string): string {
  return digits.slice(0, -1);
}

/**
 * The digit run rendered as fixed-width slots, so the display does not reflow
 * as the value grows and the eye can park on one place.
 *
 * `filled` is what has been typed; `slots` is how many boxes to draw. Slots are
 * the LONGEST length still reachable from here, which is what tells the player
 * there is room for another digit.
 *
 * IT USED TO BE THE SHORTEST, AND THAT LOST A GOLDEN ROUND. Every prefix that
 * is already a legal value returned its own length, so the pad drew exactly as
 * many boxes as had been typed and always looked complete. Asked for China's
 * population, a player who opened with 9 saw one box, then two, then three —
 * and at 999 every key greyed out, because no fourth digit can keep a 9 under
 * the 1500 ceiling. The pad was arithmetically right and said nothing: there
 * was no way to see that 1400 was typable and 9000 never had been. Reported
 * from a live daily as "I could only put three digits".
 *
 * Showing the longest reachable length says both things at once. Open with 1
 * and four boxes appear, so the room is visible before it is needed; open with
 * 9 and three appear, so the ceiling is visible instead of being discovered by
 * a dead keypad.
 */
export function displaySlots(spec: NumericAnswerSpec, digits: string): number {
  const limit = maxDigits(spec);
  // A leading zero is only ever the whole answer, so it never grows.
  if (digits.startsWith('0')) return 1;

  let longest = 0;
  for (let len = Math.max(1, digits.length); len <= limit; len += 1) {
    const scale = pow10(len - digits.length);
    const p = digits === '' ? 0 : Number(digits);
    const lo = p * scale;
    const hi = lo + scale - 1;
    if (hi >= spec.min && lo <= spec.max) longest = len;
  }
  // Nothing reachable: keep the boxes already typed rather than collapsing the
  // display out from under the digits that are in it.
  return longest === 0 ? Math.max(digits.length, 1) : longest;
}
